import {List} from 'src/pages/lists/hooks/useLists'
import axios from './_axios'
import {prepareParams} from './_internal'
import {AxiosResult, Ord, Paginate} from './_types'
import {CreateListDTO} from './FacialRecognitionEndpoint'

const prefix = process.env.REACT_APP_LICENSE_PLATES_BASE_URL

export type Lpr = {
  id: number
  plate: LicensePlate
  createdAt: string
  camera: CameraOnLpr
  sentToSpia: boolean
  sentToHarpia: boolean
  sentToAspide: boolean
  sentToSivana: null | number
  score: number
  imageAwsKey: string
  croppedImageAwsKey: string
  sentToMuralhaPaulista: boolean
}

type CameraOnLpr = {
  id: number
  name: string
  companyId: number
}

export type LprInfos = {
  id: number
  ano: string
  municipio: string
  uf: string
  situacao: string
  camera: {
    name: string
  }
}

export type LprFilterType = {
  cameraIds?: number[]
  companyIds?: number[]
  clientIds?: number[]
  color?: string
  model?: string
  plates?: string[]
  city?: string
  brand?: string
}

export type LicensePlate = {
  id: number
  licensePlate: string
  vehicle: Vehicle
}

export type LPRSituation = {
  id: number
  name: string
}

export type Vehicle = {
  id: number
  plateId: number
  brand: string
  model: string
  year: string
  color: string
}

export type GetLPRParams = {
  batchSize?: number
  startId?: number | null
  includes?: string[]
  filter?: LprFilterType
  start?: string
  end?: string
  sentToHarpia?: boolean
  sentToSpia?: boolean
  sentToSivana?: boolean
  orderBy?: {
    createdAt: Ord
  }
}

export type OrderingTypes = 'ASC' | 'DESC'

type LicensePlateListIncludes = 'action'

export type GetLicensePlateListsParams = {
  includes?: LicensePlateListIncludes[]
  filter?: {
    ids?: number[]
    clientIds?: number[]
    companyIds?: Array<number>
  }
  orderBy?: {createdAt?: OrderingTypes}
  search?: {listName?: string}
  page?: number
  pageSize?: number
}

export type LicensePlateList = {
  id: number
  name: string
  clientId: number
  companyId: number
  actionId: number
  createdAt: Date
  listColor?: string
}

export type LicensePlateHasList = {
  listId: number
  licensePlate: string
  description: string
}

type PlateDTO = {
  licensePlate: string
  description: string
}

type PlateIncludes = 'actions'

export type GetPlatesParams = {
  includes?: PlateIncludes[]
  filter?: {
    ids?: number[]
    clientIds?: number[]
  }
  orderBy?: {createdAt?: OrderingTypes}
  search?: {listName?: string}
  page?: number
  pageSize?: number
}

export class LprEndpoint {
  async getManyLists(params: GetLicensePlateListsParams): Promise<AxiosResult<Paginate<LicensePlateList>>> {
    return await axios.get(`${prefix}/license-plate-list`, {
      params: prepareParams(params),
    })
  }

  async deleteManyPlateLists(ids: number[]): Promise<AxiosResult<any>> {
    return axios.delete(`${prefix}/license-plate-list`, {data: {ids}})
  }

  async createLprList(params: CreateListDTO): Promise<AxiosResult<LicensePlateList>> {
    return await axios.post(`${prefix}/license-plate-list`, params)
  }

  async updateLprList(id: number, body: Partial<List>): Promise<AxiosResult<any>> {
    return await axios.patch(`${prefix}/license-plate-list/${id}`, body)
  }

  async getPlates(id: string, params: GetPlatesParams): Promise<AxiosResult<Paginate<LicensePlateHasList>>> {
    return await axios.get(`${prefix}/license-plate-list/${id}/plates`, {
      params: prepareParams(params),
    })
  }

  async createPlate(id: string, plateDTO: PlateDTO): Promise<AxiosResult<LicensePlateHasList>> {
    return await axios.put(`${prefix}/license-plate-list/${id}/plate`, plateDTO)
  }

  async deleteList(id: number): Promise<AxiosResult<LicensePlateList>> {
    return await axios.delete(`${prefix}/license-plate-list/${id}`)
  }

  async deletePlate(listId: number, plate: string): Promise<AxiosResult<LicensePlateHasList>> {
    return await axios.delete(`${prefix}/license-plate-list/${listId}/plate`, {data: {licensePlate: plate}})
  }

  async getCsv(params: GetLPRParams): Promise<Blob> {
    const result = await axios.get(`${prefix}/license-plate-detections/csv`, {
      params: prepareParams(params),
      timeout: 60000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    const blob = new Blob([result.data.data], {
      type: 'text/csv;charset=utf-8;',
    })
    return blob
  }
}
